// API para persistir los chats del Asistente V2 en la tabla `agent_chats`.
//
// Columnas: id, user_id, id_company, title, messages (jsonb), created_at,
// updated_at, total_credits_used, project_id (nullable: el proyecto del Estudio de
// Video en el que se creó el chat — ADR-2026-09-11-el-chat-del-proyecto-de-video) y
// lienzo (jsonb, nullable: posiciones del lienzo del Agente —
// ADR-2026-09-16-el-lienzo-se-guarda-por-chat).
// RLS por empresa (el front escribe con el token del usuario, así que cada uno solo
// toca los chats de su company); `updated_at` lo bumpea un trigger BEFORE UPDATE.
//
// IMPORTANTE: en `messages` se guardan los V2ChatMessage tal cual — SOLO con
// `image_ids` en las cards de resultado (NUNCA URLs firmadas, que expiran).
// Las URLs se vuelven a firmar al renderizar.

import { supabase } from '../../lib/supabase';
import { normalizarLienzo, type LienzoGuardado } from '../../chat-atlas/lienzo/persistenciaDelLienzo';
import type { V2ChatMessage } from './useV2Chat';
import type { VisibilidadDeChat } from './chatCompartido';

const TITLE_MAX = 80;

/**
 * Saca de los mensajes lo que NO puede vivir en la DB: las URLs firmadas de
 * los adjuntos de la burbuja del usuario
 * (ADR-2026-09-08-los-adjuntos-quedan-en-la-burbuja).
 *
 * Es la misma regla de arriba —nunca guardar URLs firmadas— aplicada al campo
 * nuevo: vencen en una hora, así que en la DB son bytes muertos que además
 * mienten. El resto del adjunto (tipo y nombre) sí se guarda: al abrir el chat
 * del historial la burbuja muestra el recuadro con su ícono, que sigue diciendo
 * que ahí iba un adjunto. Dentro de la sesión no cambia nada — sessionStorage
 * y el state guardan el mensaje completo, con miniatura.
 */
const sinUrlsFirmadas = (messages: V2ChatMessage[]): V2ChatMessage[] =>
  messages.map((m) =>
    m.attachments && m.attachments.length > 0
      ? {
          ...m,
          attachments: m.attachments.map(({ previewUrl: _, ...resto }) => resto),
        }
      : m,
  );

export interface AgentChatSummary {
  id: string;
  title: string | null;
  updated_at: string;
  /** De quién es el chat. La lista es POR EMPRESA (ver `listChats`), así que
   *  sin esto la UI no puede distinguir los propios de los de un compañero —
   *  y el borrado terminaba alcanzando los ajenos
   *  (ADR-2026-09-11-el-borrado-del-historial-no-cruza-usuarios). */
  user_id: string | null;
  /** 'empresa' = lo ven y lo siguen los compañeros (ADR-2026-10-13-chats-compartidos-con-la-empresa). */
  visibilidad?: VisibilidadDeChat;
  /** Cuándo ganó un mensaje por última vez: la fecha que muestra y ordena el historial.
   *  Ausente mientras la migración no esté aplicada (ver `fechaDelChat`). */
  ultimo_mensaje_en?: string | null;
}

/**
 * La fecha de un chat en el historial — ADR-2026-10-17-la-fecha-del-chat-es-la-del-ultimo-mensaje.
 * La del último mensaje, no `updated_at`: ésa cambia con cualquier guardado (abrir el chat
 * vuelve a guardar los mensajes, el lienzo…) y es la versión del chat compartido.
 */
export const fechaDelChat = (c: { updated_at: string; ultimo_mensaje_en?: string | null }): string =>
  c.ultimo_mensaje_en ?? c.updated_at;

/** ¿El error es que la columna `ultimo_mensaje_en` todavía no existe (migración sin aplicar)? */
const faltaUltimoMensaje = (e: { code?: string; message?: string } | null): boolean =>
  !!e && (e.code === '42703' || (e.message ?? '').includes('ultimo_mensaje_en'));

/**
 * Qué es un chat cuyo `tipo` no es null — ADR-2026-09-17-un-diseno-es-un-chat. Un diseño
 * del modo "Diseño de indumentaria" ES una fila de `agent_chats`: su lienzo es la pantalla
 * del diseño y sus mensajes, lo que se habló con el Agente adentro de él.
 */
export const TIPO_CHAT_DISENO = 'diseno_indumentaria';
/**
 * Una sesión del modo Redes también es un chat (ADR-2026-10-01-modo-redes): su lienzo es lo
 * que se generó en esa sesión y sus mensajes, lo que se habló con el Asistente de Crear. A
 * diferencia de los diseños, SÍ aparece en el historial del Agente cuando tiene conversación:
 * es el mismo asistente (ADR-2026-10-18-un-solo-historial-con-lo-de-principal, ver
 * `FILTRO_DEL_HISTORIAL`).
 */
export const TIPO_CHAT_REDES = 'redes';
/**
 * Una sesión del modo Catálogo (Hogar y Decoración) también es un chat
 * (ADR-2026-10-13-catalogo-con-el-agente): su `lienzo` guarda el plan (`{ catalogo: … }`) y sus
 * mensajes, lo que se habló con el Agente para armarlo. No aparece en el historial del Agente.
 */
export const TIPO_CHAT_CATALOGO = 'catalogo';
/**
 * Una sesión del modo Videos cortos también es un chat (ADR-2026-10-30-videos-cortos-con-el-agente):
 * su `lienzo.videos_cortos` guarda el plan de la tanda. Tampoco aparece en el historial.
 */
export const TIPO_CHAT_VIDEOS_CORTOS = 'videos-cortos';

/**
 * La versión de una fila es su `updated_at` crudo, tal como lo devuelve PostgREST (con
 * microsegundos). Nunca pasa por `Date`: perdería precisión y el guardado con versión
 * dejaría de coincidir.
 */
export type VersionDeChat = string;

/** Crea un chat nuevo (primer mensaje) y devuelve su id y su versión. */
export async function crearChatConVersion(args: {
  userId: string;
  companyId: string;
  title: string;
  messages: V2ChatMessage[];
  /** El proyecto del Estudio de Video en el que se creó, si fue adentro de uno. */
  projectId?: string | null;
  /** `TIPO_CHAT_DISENO` para un diseño; sin esto es un chat del Agente. */
  tipo?: string | null;
}): Promise<{ id: string; version: VersionDeChat } | null> {
  const title = args.title.trim().slice(0, TITLE_MAX);
  const { data, error } = await supabase
    .from('agent_chats')
    .insert({
      user_id: args.userId,
      id_company: args.companyId,
      title: title || null,
      messages: sinUrlsFirmadas(args.messages),
      ...(args.projectId ? { project_id: args.projectId } : {}),
      ...(args.tipo ? { tipo: args.tipo } : {}),
    })
    .select('id, updated_at')
    .single();
  if (error) {
    console.error('[agentChatApi] createChat', error);
    return null;
  }
  const fila = data as { id: string; updated_at: string } | null;
  return fila?.id ? { id: fila.id, version: fila.updated_at } : null;
}

/** Crea un chat nuevo (primer mensaje) y devuelve su id. */
export async function createChat(
  args: Parameters<typeof crearChatConVersion>[0],
): Promise<string | null> {
  return (await crearChatConVersion(args))?.id ?? null;
}

/**
 * Guarda los mensajes SÓLO si nadie guardó desde la versión que leí
 * (ADR-2026-10-13-chats-compartidos-con-la-empresa). Un chat compartido lo pueden seguir
 * varias personas y el hilo entero es una sola fila: sin esto, gana el último que guarda
 * y el otro pierde sus mensajes. Sin versión conocida (`null`) guarda sin condición y
 * devuelve la versión nueva, para que el próximo guardado ya vaya condicionado.
 *
 * `conflicto` también sale cuando la fila ya no se puede actualizar (el dueño la volvió
 * privada): quien llama relee y, si tampoco la puede leer, se rinde.
 */
export async function guardarMensajesSiNoCambio(
  chatId: string,
  messages: V2ChatMessage[],
  versionLeida: VersionDeChat | null,
): Promise<{ ok: true; version: VersionDeChat } | { ok: false; motivo: 'conflicto' | 'error' }> {
  let q = supabase
    .from('agent_chats')
    .update({ messages: sinUrlsFirmadas(messages) })
    .eq('id', chatId);
  if (versionLeida) q = q.eq('updated_at', versionLeida);
  const { data, error } = await q.select('updated_at');
  if (error) {
    console.error('[agentChatApi] guardarMensajesSiNoCambio', error);
    return { ok: false, motivo: 'error' };
  }
  const fila = (data as { updated_at: string }[] | null)?.[0];
  if (!fila) return { ok: false, motivo: 'conflicto' };
  return { ok: true, version: fila.updated_at };
}

/**
 * El chat entero con su versión y su dueño: lo que hace falta para seguirlo. No pide
 * `visibilidad` a propósito: es lo que corre en cada apertura de chat, y así el front no
 * depende de que la migración de la columna ya esté aplicada. La columna sólo la leen
 * `leerVisibilidad` y `fijarVisibilidad`, que son del menú y fallan cerrado.
 */
export async function cargarChatConVersion(chatId: string): Promise<{
  messages: V2ChatMessage[];
  version: VersionDeChat;
  userId: string | null;
} | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('messages, updated_at, user_id')
    .eq('id', chatId)
    .single();
  if (error) {
    console.error('[agentChatApi] cargarChatConVersion', error);
    return null;
  }
  const fila = data as { messages: unknown; updated_at: string; user_id: string | null };
  return {
    messages: Array.isArray(fila.messages) ? (fila.messages as V2ChatMessage[]) : [],
    version: fila.updated_at,
    userId: fila.user_id ?? null,
  };
}

/** Dueño y visibilidad, sin bajar los mensajes: para el menú del chat. Sin la columna
 *  (migración todavía no aplicada) devuelve null y el menú no ofrece la opción. */
export async function leerVisibilidad(
  chatId: string,
): Promise<{ duenoId: string | null; visibilidad: VisibilidadDeChat } | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('user_id, visibilidad')
    .eq('id', chatId)
    .maybeSingle();
  if (error || !data) return null;
  const fila = data as { user_id: string | null; visibilidad: VisibilidadDeChat | null };
  return { duenoId: fila.user_id ?? null, visibilidad: fila.visibilidad ?? 'privado' };
}

/** Sólo el dueño: el `with check` de los compañeros no deja volverlo privado. */
export async function fijarVisibilidad(
  chatId: string,
  visibilidad: VisibilidadDeChat,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('agent_chats')
    .update({ visibilidad })
    .eq('id', chatId)
    .select('id');
  if (error) {
    console.error('[agentChatApi] fijarVisibilidad', error);
    return false;
  }
  return ((data as unknown[] | null)?.length ?? 0) > 0;
}

/** Actualiza los mensajes de un chat existente (updated_at lo bumpea el trigger). */
export async function updateChatMessages(
  chatId: string,
  messages: V2ChatMessage[],
): Promise<void> {
  const { error } = await supabase
    .from('agent_chats')
    .update({ messages: sinUrlsFirmadas(messages) })
    .eq('id', chatId);
  if (error) console.error('[agentChatApi] updateChatMessages', error);
}

/** Carga los mensajes de un chat por id (para abrir uno del historial). */
export async function loadChat(
  chatId: string,
): Promise<V2ChatMessage[] | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('messages')
    .eq('id', chatId)
    .single();
  if (error) {
    console.error('[agentChatApi] loadChat', error);
    return null;
  }
  const msgs = (data as any)?.messages;
  return Array.isArray(msgs) ? (msgs as V2ChatMessage[]) : [];
}

/**
 * ¿La sesión de Crear está VACÍA (sin mensajes y sin nada en el lienzo)? Entrar a "Crear
 * desde cero" la reusa en vez de abrir otra, así no se juntan sesiones vacías
 * (ADR-2026-10-13-crear-desde-cero-abre-un-chat-nuevo). Ante un error, `false`: mejor una
 * sesión de más que abrir una con cosas.
 */
export async function sesionDeRedesVacia(chatId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('messages, lienzo')
    .eq('id', chatId)
    .single();
  if (error || !data) return false;
  const fila = data as { messages?: unknown; lienzo?: unknown };
  const sinMensajes = !Array.isArray(fila.messages) || fila.messages.length === 0;
  const lienzo = fila.lienzo ? normalizarLienzo(fila.lienzo) : null;
  const lienzoVacio =
    !lienzo || (lienzo.tandas.length === 0 && lienzo.anotaciones.length === 0 && lienzo.trazos.length === 0);
  return sinMensajes && lienzoVacio;
}

/**
 * El lienzo guardado de un chat, crudo: lo normaliza quien lo usa
 * (`normalizarLienzo`), así un jsonb roto no puede tirar nada acá. `null` = nunca se
 * guardó (o el chat no existe).
 */
export async function leerLienzo(chatId: string): Promise<unknown | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('lienzo')
    .eq('id', chatId)
    .single();
  if (error) {
    console.error('[agentChatApi] leerLienzo', error);
    return null;
  }
  return (data as { lienzo?: unknown } | null)?.lienzo ?? null;
}

/**
 * El título del chat. Un diseño lo tiene desde que nace (lo escribe la portada), así que
 * es el nombre correcto aunque el lienzo todavía no haya cargado ninguna imagen propia
 * —el caso de una prenda descrita en la caja de prompt.
 */
export async function leerTituloDeChat(chatId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('title')
    .eq('id', chatId)
    .single();
  if (error) {
    console.error('[agentChatApi] leerTituloDeChat', error);
    return null;
  }
  return (data as { title?: string | null } | null)?.title ?? null;
}

/**
 * El `tipo` de un chat: null es del Agente, `TIPO_CHAT_REDES` una sesión de Crear,
 * `TIPO_CHAT_DISENO` un diseño. `undefined` si no se pudo leer.
 */
export async function leerTipoDeChat(chatId: string): Promise<string | null | undefined> {
  const { data, error } = await supabase.from('agent_chats').select('tipo').eq('id', chatId).single();
  if (error) {
    console.error('[agentChatApi] leerTipoDeChat', error);
    return undefined;
  }
  return (data as { tipo?: string | null } | null)?.tipo ?? null;
}

/** Cambia el título de un chat (el nombre de un diseño). Devuelve si se guardó. */
export async function renombrarChat(chatId: string, titulo: string): Promise<boolean> {
  const title = titulo.trim().slice(0, TITLE_MAX);
  if (!title) return false;
  const { error } = await supabase.from('agent_chats').update({ title }).eq('id', chatId);
  if (error) {
    console.error('[agentChatApi] renombrarChat', error);
    return false;
  }
  return true;
}

/**
 * Guarda el lienzo entero (posiciones + oculto). `updated_at` no importa acá. Devuelve si
 * se guardó: quien CREA algo con esto (un diseño) tiene que saber que quedó a medias, y
 * el autoguardado del lienzo lo sigue ignorando sin cambiar nada.
 */
export async function guardarLienzo(chatId: string, lienzo: LienzoGuardado): Promise<boolean> {
  const { error } = await supabase.from('agent_chats').update({ lienzo }).eq('id', chatId);
  if (error) {
    console.error('[agentChatApi] guardarLienzo', error);
    return false;
  }
  return true;
}

/** Suma créditos al tally del chat (costo de imágenes generadas). El costo
 *  del agente (LLM) lo suma el edge function `chat-agent`. */
export async function addChatCredits(
  chatId: string,
  credits: number,
): Promise<void> {
  if (credits <= 0) return;
  const { error } = await supabase.rpc('increment_agent_chat_credits', {
    p_chat_id: chatId,
    p_credits: credits,
  });
  if (error) console.error('[agentChatApi] addChatCredits', error);
}

/** Lee los créditos totales consumidos en un chat (agente + imágenes). */
export async function fetchChatCredits(chatId: string): Promise<number> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('total_credits_used')
    .eq('id', chatId)
    .single();
  if (error) {
    console.error('[agentChatApi] fetchChatCredits', error);
    return 0;
  }
  return Number((data as any)?.total_credits_used ?? 0);
}

/**
 * El chat más reciente de un proyecto del Estudio de Video, o null si ese proyecto no
 * tiene ninguno (ADR-2026-09-11-el-chat-del-proyecto-de-video).
 */
export async function buscarChatDelProyecto(projectId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('id')
    .eq('project_id', projectId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error('[agentChatApi] buscarChatDelProyecto', error);
    return null;
  }
  return ((data as any)?.id as string) ?? null;
}

/**
 * Ata un chat que ya existe a un proyecto del Estudio de Video. Lo usa la tarjeta
 * `ir_al_estudio` ANTES de navegar: así, al abrir el chat adentro del editor,
 * `buscarChatDelProyecto` encuentra ESTE chat y la conversación sigue en vez de arrancar
 * una nueva (ADR-2026-10-11-ir-al-estudio-desde-el-chat). Un chat que todavía no tiene
 * fila no necesita esto: `createChat` lee el proyecto de la ruta al crearse.
 */
export async function asociarChatAProyecto(chatId: string, projectId: string): Promise<boolean> {
  const { error } = await supabase.from('agent_chats').update({ project_id: projectId }).eq('id', chatId);
  if (error) {
    console.error('[agentChatApi] asociarChatAProyecto', error);
    return false;
  }
  return true;
}

/**
 * Borra chats del historial por id — ADR-2026-09-08-borrar-el-historial-de-chats.
 *
 * Devuelve los ids REALMENTE borrados, no un booleano de "no hubo error". El motivo es
 * RLS: la policy de DELETE de `agent_chats` filtra por `id_company`, y un `delete()`
 * que no matchea ninguna fila **no es un error** — devuelve `error: null` y borra cero.
 * Sin el `.select()` la UI sacaría la fila de la lista y el chat seguiría vivo en la
 * base, reapareciendo en el próximo `listChats()`.
 *
 * Sirve para uno o para muchos (el "Borrar todo" manda los ids que la lista tiene a la
 * vista, en vez de un `delete()` abierto: así lo que se borra es exactamente lo que el
 * usuario está mirando).
 *
 * **Filtra por `user_id` además de por id.** La RLS de `agent_chats` es por EMPRESA, no
 * por usuario: sin este filtro, mandar el id de un chat de un compañero lo borraba de
 * verdad —y "Borrar todo" mandaba los ids de toda la lista, que es la de la empresa
 * entera—. Es irreversible y no hay papelera, así que el filtro va acá abajo y no sólo
 * en la UI: un caller que se olvide de filtrar no puede pasarse de largo
 * (ADR-2026-09-11-el-borrado-del-historial-no-cruza-usuarios).
 */
export async function deleteChats(
  ids: string[],
  userId: string,
): Promise<string[]> {
  if (ids.length === 0 || !userId) return [];
  const { data, error } = await supabase
    .from('agent_chats')
    .delete()
    .in('id', ids)
    .eq('user_id', userId)
    .select('id');
  if (error) {
    console.error('[agentChatApi] deleteChats', error);
    return [];
  }
  return ((data as { id: string }[] | null) ?? []).map((r) => r.id);
}

/**
 * Qué entra en el historial de conversaciones (ADR-2026-10-18-un-solo-historial-con-lo-de-principal).
 * El Agente (`/agente`) y el Asistente de Crear (`/redes/:id`) son el mismo asistente —un motor,
 * una tabla— y muestran el MISMO historial: los chats del Agente (`tipo` null) y las sesiones de
 * Crear donde se charló. Las de la caja (Imagen y Video) nacen con `messages: []` y guardan lo
 * generado en el lienzo: no son conversaciones y abrirían una grilla
 * (ADR-2026-10-17-el-historial-del-asistente-abre-conversaciones). Los diseños y los catálogos
 * (`tipo` propio) siguen fuera: se listan en la portada de su modo.
 */
const FILTRO_DEL_HISTORIAL = `tipo.is.null,and(tipo.eq.${TIPO_CHAT_REDES},messages.neq.[])`;

/** Lista los chats de la empresa del usuario, recientes primero (historial).
 *
 *  OJO: el alcance es la EMPRESA, no el usuario — la lista incluye los chats de
 *  los compañeros. Trae `user_id` justamente para que la UI pueda distinguirlos
 *  y no ofrecer borrar los ajenos.
 *
 *  Es el historial de los dos lados: el del Agente y el del Asistente de Crear
 *  (ver `FILTRO_DEL_HISTORIAL`). */
export async function listChats(): Promise<AgentChatSummary[]> {
  // Por el último mensaje (ADR-2026-10-17-la-fecha-del-chat-es-la-del-ultimo-mensaje); sin
  // la columna todavía, como antes.
  const pedir = (conUltimo: boolean) =>
    supabase
      .from('agent_chats')
      .select(conUltimo ? 'id, title, updated_at, user_id, ultimo_mensaje_en' : 'id, title, updated_at, user_id')
      .or(FILTRO_DEL_HISTORIAL)
      .order(conUltimo ? 'ultimo_mensaje_en' : 'updated_at', { ascending: false });
  let { data, error } = await pedir(true);
  if (faltaUltimoMensaje(error)) ({ data, error } = await pedir(false));
  if (error) {
    console.error('[agentChatApi] listChats', error);
    return [];
  }
  return (data as unknown as AgentChatSummary[]) ?? [];
}

/**
 * ¿Este usuario ya tuvo ALGÚN chat con el Agente? — ADR-2026-09-14-la-bienvenida-del-agente.
 *
 * Decide si el arranque muestra la burbuja de bienvenida en vez del título. `null` mientras
 * no se sabe: la UI no tiene que adivinar (un saludo de "primera vez" parpadeando en la
 * cuenta de alguien con cuarenta chats es peor que aparecer un instante después).
 *
 * Por USUARIO y no por empresa, al revés que `listChats`: la lista del historial muestra los
 * chats de los compañeros a propósito, pero alguien que recién entra al equipo ESTÁ entrando
 * por primera vez aunque su empresa tenga mil. El `user_id` lo pone RLS igual; el `.eq` es
 * lo que acota de la empresa a la persona.
 *
 * `head: true` + `count: 'exact'` + `limit(1)`: no baja ninguna fila, sólo pregunta si hay.
 */
export async function usuarioTieneChats(): Promise<boolean | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  // Sólo los chats del Agente: alguien que sólo hizo diseños
  // (`tipo` no nulo) nunca habló con el Agente y le toca la bienvenida.
  const { count, error } = await supabase
    .from('agent_chats')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', user.id)
    .is('tipo', null)
    .limit(1);
  if (error) {
    console.error('[agentChatApi] usuarioTieneChats', error);
    // Ante el error se asume que SÍ tiene: no saludar de más es más barato que saludar a
    // quien ya conoce el chat.
    return true;
  }
  return (count ?? 0) > 0;
}

export interface DisenoResumen {
  id: string;
  titulo: string;
  actualizadoEn: string;
  /** La primera imagen propia del diseño (su base), para la miniatura de la portada. */
  portada: { bucket: string; path: string } | null;
}

/**
 * Los diseños de la empresa, del más reciente al más viejo
 * (ADR-2026-09-17-un-diseno-es-un-chat). Trae el `lienzo` entero para sacar la portada:
 * son pocas filas y el jsonb de un diseño es chico (posiciones, tandas e imágenes propias).
 */
export async function listDisenos(): Promise<DisenoResumen[]> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('id, title, updated_at, lienzo')
    .eq('tipo', TIPO_CHAT_DISENO)
    .order('updated_at', { ascending: false })
    .limit(50);
  if (error) {
    console.error('[agentChatApi] listDisenos', error);
    return [];
  }
  return ((data ?? []) as { id: string; title: string | null; updated_at: string; lienzo: unknown }[]).map((r) => {
    const primera = normalizarLienzo(r.lienzo).diseno?.imagenes[0];
    return {
      id: r.id,
      titulo: r.title ?? 'Diseño',
      actualizadoEn: r.updated_at,
      portada: primera ? { bucket: primera.bucket, path: primera.path } : null,
    };
  });
}

/**
 * La sesión de Crear VACÍA más nueva del usuario, para reusarla al entrar a "Crear desde cero"
 * en vez de crear otra (ADR-2026-10-13-crear-desde-cero-abre-un-chat-nuevo). Se busca por
 * `created_at` y por usuario: buscar "la última tocada" de la empresa casi nunca daba con la
 * vacía —la sesión que se acaba de usar se tocó después— y cada entrada dejaba una sesión
 * vacía más (ADR-2026-10-16-un-solo-historial-agente-y-asistente). `null` si no hay ninguna o
 * si la consulta falla: entonces se crea una.
 */
export async function sesionDeRedesVaciaParaReusar(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('id')
    .eq('tipo', TIPO_CHAT_REDES)
    .eq('user_id', userId)
    .eq('messages', '[]')
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) {
    console.error('[agentChatApi] sesionDeRedesVaciaParaReusar', error);
    return null;
  }
  const id = (data as { id: string }[] | null)?.[0]?.id;
  return id && (await sesionDeRedesVacia(id)) ? id : null;
}

export interface SesionDeRedesResumen {
  id: string;
  titulo: string;
  actualizadoEn: string;
}

/** Las sesiones del modo Redes de la empresa, de la más reciente a la más vieja: el Historial de
 *  Imagen y Video. Incluye las de la caja, que no tienen mensajes: su actividad es lo que se
 *  generó, y van por `updated_at`. Las conversaciones se listan con `listChats`. */
export async function listSesionesRedes(): Promise<SesionDeRedesResumen[]> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select('id, title, updated_at')
    .eq('tipo', TIPO_CHAT_REDES)
    .order('updated_at', { ascending: false })
    .limit(30);
  if (error) {
    console.error('[agentChatApi] listSesionesRedes', error);
    return [];
  }
  return ((data ?? []) as unknown as { id: string; title: string | null; updated_at: string; ultimo_mensaje_en?: string | null }[]).map((r) => ({
    id: r.id,
    titulo: r.title ?? 'Sesión',
    actualizadoEn: fechaDelChat(r),
  }));
}

export interface SesionDeCatalogoResumen {
  id: string;
  titulo: string;
  actualizadoEn: string;
}

interface FilaDeSesionCatalogo {
  id: string;
  title: string | null;
  updated_at: string;
  /** `lienzo->catalogo->>titulo`: el título del plan, el que la persona y el agente editan. */
  titulo_plan?: string | null;
}

/** Una fila → su resumen. El título es el del PLAN: el del chat queda con el que se creó la
 *  sesión ("Catálogo 14 oct") y no se actualiza al cambiarlo. Puro. */
export function resumenDeSesionCatalogo(r: FilaDeSesionCatalogo): SesionDeCatalogoResumen {
  return {
    id: r.id,
    titulo: r.titulo_plan?.trim() || r.title?.trim() || 'Catálogo',
    actualizadoEn: r.updated_at,
  };
}

/**
 * Las sesiones de un modo con plan en el lienzo (Catálogo, Videos cortos), de la más reciente a
 * la más vieja. `claveDelLienzo` es dónde vive el plan (`catalogo`, `videos_cortos`): el título
 * que se lista es el del plan.
 */
export async function listSesionesDeModo(tipo: string, claveDelLienzo: string, respaldo = 'Sesión'): Promise<SesionDeCatalogoResumen[]> {
  const { data, error } = await supabase
    .from('agent_chats')
    .select(`id, title, updated_at, titulo_plan:lienzo->${claveDelLienzo}->>titulo`)
    .eq('tipo', tipo)
    .order('updated_at', { ascending: false })
    .limit(30);
  if (error) {
    console.error('[agentChatApi] listSesionesDeModo', tipo, error);
    return [];
  }
  return ((data ?? []) as unknown as FilaDeSesionCatalogo[]).map((r) => ({ ...resumenDeSesionCatalogo(r), titulo: r.titulo_plan?.trim() || r.title?.trim() || respaldo }));
}

/** Las sesiones del modo Catálogo de la empresa, de la más reciente a la más vieja. */
export async function listSesionesCatalogo(): Promise<SesionDeCatalogoResumen[]> {
  return listSesionesDeModo(TIPO_CHAT_CATALOGO, 'catalogo', 'Catálogo');
}

/** Las sesiones del modo Videos cortos de la empresa. */
export async function listSesionesVideosCortos(): Promise<SesionDeCatalogoResumen[]> {
  return listSesionesDeModo(TIPO_CHAT_VIDEOS_CORTOS, 'videos_cortos', 'Videos');
}
